import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { gigSwal, escapeHtml } from '../utils/swal';
import CSVImportModal from '../components/CSVImportModal';
import { FileText, Music, BookOpen } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import PageLoader from '../components/PageLoader';
import { useCanEdit } from '../context/BandContext';
import OfflineBanner from '../components/OfflineBanner';
import { loadWithSavedCopy } from '../utils/offlineCache';
import { READINESS, READINESS_KEYS, setReadiness, dismissReadinessHint, readinessHint, dueForReview, formatDay } from '../utils/readiness';

// "Review your songs" card snoozes for 30 days on this device
const REVIEW_CARD_SNOOZE_KEY = 'gigrequest:reviewCardSnoozedUntil';
const readSnooze = () => { try { return Number(localStorage.getItem(REVIEW_CARD_SNOOZE_KEY)) || 0; } catch { return 0; } };

const API_URL = process.env.REACT_APP_API_URL || '';

function SongManagement() {
  const theme = useTheme();

  const fieldStyle = {
    width: '100%', padding: '0.75rem', border: `2px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box',
    background: theme.surface, color: theme.text,
  };
  const fieldLabelStyle = {
    display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: theme.text,
  };

  const canEdit = useCanEdit();  // performers can read songs (lyrics, chords) but not change them
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSong, setEditingSong] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [genreFilter, setGenreFilter] = useState('all');
  const [editTab, setEditTab] = useState('details');
  const [menuSongId, setMenuSongId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [fabOpen, setFabOpen] = useState(false);
  const [genreLookingUp, setGenreLookingUp] = useState(false);
  const [fillModalOpen, setFillModalOpen] = useState(false);
  const [fillResults, setFillResults] = useState([]);
  const [fillFetching, setFillFetching] = useState(false);
  const [savingFill, setSavingFill] = useState(false);
  const [offlineSince, setOfflineSince] = useState(null);
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [lyricsResults, setLyricsResults] = useState([]);
  const [lyricsFetching, setLyricsFetching] = useState(false);
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [lyricsLookingUp, setLyricsLookingUp] = useState(false);
  // Readiness: every band member may set it, performers included (curation spec §3.1a)
  const [readinessFilter, setReadinessFilter] = useState('all');
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [reviewCardSnoozedUntil, setReviewCardSnoozedUntil] = useState(readSnooze);

  const [formData, setFormData] = useState({
    title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '', is_original: false,
  });

  useEffect(() => { fetchSongs({ emptyPage: true }); }, []);

  useEffect(() => {
    const handler = () => { setMenuSongId(null); setFabOpen(false); };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  // No signal: the saved library (with lyrics) covers songs added to a set on the night
  const fetchSongs = async (opts) => {
    try {
      const cachedAt = await loadWithSavedCopy('songs', async () => {
        const response = await axios.get(`${API_URL}/api/songs`);
        return Array.isArray(response.data) ? response.data : [];
      }, data => { setSongs(data); setLoading(false); }, opts);
      setOfflineSince(cachedAt);
    } catch (err) {
      console.error('Error fetching songs:', err);
      setLoading(false);
    }
  };

  const openAddWizard = () => {
    setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '', is_original: false });
    setWizardStep(1);
    setWizardOpen(true);
    setEditingSong(null);
  };

  const closeWizard = () => { setWizardOpen(false); };

  const lookupSpotifyGenre = async () => {
    if (!formData.title) return;
    setGenreLookingUp(true);
    try {
      const res = await axios.post(`${API_URL}/api/spotify/genre-lookup`, {
        title: formData.title, artist: formData.artist_original,
      });
      if (res.data.genre || res.data.durationMs) {
        const updates = {};
        if (res.data.genre) updates.genre = res.data.genre;
        if (res.data.durationMs) {
          const s = Math.round(res.data.durationMs / 1000);
          updates.duration = `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
        }
        setFormData(f => ({ ...f, ...updates }));
        const genreLabel = res.data.genre
          || (res.data.spotifyGenres?.length ? `Unmatched: ${res.data.spotifyGenres[0]}` : 'No genres on Spotify');
        toast.success(`${genreLabel}${res.data.matchedTrack ? ` · matched "${res.data.matchedTrack}"` : ''}`);
      } else {
        toast.error('No Spotify match found');
      }
    } catch {
      toast.error('Spotify lookup failed');
    } finally {
      setGenreLookingUp(false);
    }
  };

  const openFillModal = async () => {
    const missing = songs.filter(s => !s.is_original && (!s.genre || !s.duration_seconds));
    if (missing.length === 0) { toast('All songs are fully enriched!'); return; }
    const initial = missing.map(s => ({ song: s, suggestedGenre: '', durationSecs: null, accepted: false, loading: true, error: false }));
    setFillResults(initial);
    setFillModalOpen(true);
    setFillFetching(true);
    const results = initial.map(x => ({ ...x }));
    for (let i = 0; i < missing.length; i++) {
      try {
        const res = await axios.post(`${API_URL}/api/spotify/genre-lookup`, {
          title: missing[i].title, artist: missing[i].artist_original,
        });
        const durationSecs = res.data.durationMs ? Math.round(res.data.durationMs / 1000) : null;
        const genre = res.data.genre || '';
        const hasUsefulGenre = !!genre && genre !== 'Other';
        const hasUsefulDuration = !!durationSecs && !missing[i].duration_seconds;
        results[i] = { ...results[i], suggestedGenre: genre, durationSecs, loading: false, accepted: hasUsefulGenre || hasUsefulDuration };
      } catch {
        results[i] = { ...results[i], suggestedGenre: '', loading: false, error: true, accepted: false };
      }
      setFillResults([...results]);
      if (i < missing.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setFillFetching(false);
  };

  const saveFill = async () => {
    const toSave = fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds)));
    if (toSave.length === 0) return;
    setSavingFill(true);
    try {
      await Promise.all(toSave.map(r =>
        axios.put(`${API_URL}/api/songs/${r.song.id}`, {
          title: r.song.title, artist_original: r.song.artist_original,
          genre: r.suggestedGenre.trim() || r.song.genre || null,
          is_active: r.song.is_active,
          duration_seconds: (r.durationSecs && !r.song.duration_seconds) ? r.durationSecs : (r.song.duration_seconds || null),
          notes: r.song.notes,
        })
      ));
      toast.success(`Updated ${toSave.length} song${toSave.length !== 1 ? 's' : ''}`);
      setFillModalOpen(false);
      fetchSongs();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingFill(false);
    }
  };

  const openLyricsModal = async () => {
    const missing = songs.filter(s => !s.is_original && !s.lyrics);
    if (missing.length === 0) { toast('All songs already have lyrics!'); return; }
    const initial = missing.map(s => ({ song: s, lyrics: null, matchedTitle: null, matchedArtist: null, accepted: false, loading: true, error: false }));
    setLyricsResults(initial);
    setLyricsModalOpen(true);
    setLyricsFetching(true);
    const results = initial.map(x => ({ ...x }));
    for (let i = 0; i < missing.length; i++) {
      try {
        const res = await axios.post(`${API_URL}/api/genius/lyrics-lookup`, {
          title: missing[i].title, artist_original: missing[i].artist_original,
        });
        results[i] = { ...results[i], lyrics: res.data.lyrics || null, matchedTitle: res.data.matchedTitle, matchedArtist: res.data.matchedArtist, loading: false, accepted: !!res.data.lyrics, error: !res.data.lyrics };
      } catch {
        results[i] = { ...results[i], loading: false, error: true, accepted: false };
      }
      setLyricsResults([...results]);
      if (i < missing.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    setLyricsFetching(false);
  };

  const saveLyrics = async () => {
    const toSave = lyricsResults.filter(r => r.accepted && r.lyrics);
    if (toSave.length === 0) return;
    setSavingLyrics(true);
    try {
      await Promise.all(toSave.map(r =>
        axios.put(`${API_URL}/api/songs/${r.song.id}`, {
          title: r.song.title, artist_original: r.song.artist_original,
          genre: r.song.genre, is_active: r.song.is_active,
          duration_seconds: r.song.duration_seconds, notes: r.song.notes,
          lyrics: r.lyrics, chords: r.song.chords,
        })
      ));
      toast.success(`Lyrics saved for ${toSave.length} song${toSave.length !== 1 ? 's' : ''}`);
      setLyricsModalOpen(false);
      fetchSongs();
    } catch {
      toast.error('Failed to save lyrics');
    } finally {
      setSavingLyrics(false);
    }
  };

  const lookupGeniusLyrics = async () => {
    if (!formData.title || !formData.artist_original) return;
    setLyricsLookingUp(true);
    try {
      const res = await axios.post(`${API_URL}/api/genius/lyrics-lookup`, {
        title: formData.title, artist_original: formData.artist_original,
      });
      if (res.data.lyrics) {
        setFormData(prev => ({ ...prev, lyrics: res.data.lyrics }));
        toast.success(`Matched "${res.data.matchedTitle}"`);
      } else {
        toast.error('No lyrics found on Genius');
      }
    } catch {
      toast.error('Lyrics lookup failed');
    } finally {
      setLyricsLookingUp(false);
    }
  };

  const handleAddSong = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/songs`, {
        title: formData.title,
        artist_original: formData.artist_original,
        genre: formData.genre,
        duration_seconds: mmssToSeconds(formData.duration),
        notes: formData.notes || null,
        is_original: formData.is_original,
      });
      setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '', is_original: false });
      setWizardOpen(false);
      fetchSongs();
      toast.success('Song added');
    } catch (err) {
      console.error('Error adding song:', err);
      toast.error('Failed to add song');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSong = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/songs/${editingSong.id}`, {
        title: formData.title,
        artist_original: formData.artist_original,
        genre: formData.genre,
        is_active: formData.is_active,
        duration_seconds: mmssToSeconds(formData.duration),
        notes: formData.notes || null,
        lyrics: formData.lyrics || null,
        chords: formData.chords || null,
        is_original: formData.is_original,
      });
      setEditingSong(null);
      setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '', is_original: false });
      fetchSongs();
      toast.success('Song updated successfully');
    } catch (err) {
      console.error('Error updating song:', err);
      toast.error('Failed to update song');
    }
  };

  const handleToggleActive = async (songId) => {
    try {
      await axios.patch(`${API_URL}/api/songs/${songId}/toggle`);
      fetchSongs();
    } catch (err) {
      console.error('Error toggling song:', err);
      toast.error('Failed to toggle song status');
    }
  };

  const handleDelete = async (songId, songTitle) => {
    let hasRequests = false;
    try {
      const historyRes = await axios.get(`${API_URL}/api/songs/${songId}/history`);
      hasRequests = historyRes.data.has_requests;
    } catch (err) {
      console.error('Error checking song history:', err);
      toast.error('Failed to check song history');
      return;
    }

    if (hasRequests) {
      const result = await gigSwal(theme, {
        title: 'This song has been requested before',
        html: `<p>Deleting <strong>"${escapeHtml(songTitle)}"</strong> will remove it from your analytics forever.</p><p>We recommend archiving instead to preserve your data.</p>`,
        icon: 'warning',
        showConfirmButton: true,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Archive',
        confirmButtonColor: theme.primary,
        denyButtonText: 'Delete Permanently',
        denyButtonColor: '#dc3545',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_URL}/api/songs/${songId}`);
          setSongs(prev => prev.map(s => s.id === songId ? { ...s, is_active: false } : s));
          toast.success('Song archived');
        } catch (err) {
          console.error('Error archiving song:', err);
          toast.error('Failed to archive song');
        }
      } else if (result.isDenied) {
        const confirm = await gigSwal(theme, {
          title: 'Delete permanently?',
          text: `"${songTitle}" will be removed from all analytics. This cannot be undone.`,
          icon: 'error',
          showCancelButton: true,
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'Yes, delete permanently',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        });
        if (confirm.isConfirmed) {
          try {
            await axios.delete(`${API_URL}/api/songs/${songId}?force=true`);
            setSongs(prev => prev.filter(s => s.id !== songId));
            toast.success('Song permanently deleted');
          } catch (err) {
            console.error('Error deleting song:', err);
            toast.error('Failed to delete song');
          }
        }
      }
    } else {
      const result = await gigSwal(theme, {
        title: 'Delete this song?',
        text: `"${songTitle}" has never been requested, so it's safe to delete.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_URL}/api/songs/${songId}`);
          setSongs(prev => prev.filter(s => s.id !== songId));
          toast.success('Song deleted');
        } catch (err) {
          console.error('Error deleting song:', err);
          toast.error('Failed to delete song');
        }
      }
    }
  };

  const patchSongs = (ids, fields) =>
    setSongs(prev => prev.map(s => ids.includes(s.id) ? { ...s, ...fields } : s));

  const applyReadiness = async (ids, readiness) => {
    try {
      await setReadiness(ids, readiness);
      patchSongs(ids, { readiness, readiness_reviewed_at: new Date().toISOString() });
      if (editingSong && ids.includes(editingSong.id)) setEditingSong(e => ({ ...e, readiness }));
      return true;
    } catch (err) {
      console.error('Error setting readiness:', err);
      toast.error("Couldn't save: check your signal");
      return false;
    }
  };

  const bulkReadiness = async (readiness) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (await applyReadiness(ids, readiness)) {
      toast.success(`${ids.length} song${ids.length === 1 ? '' : 's'} marked ${READINESS[readiness].label}`);
      setSelected(new Set());
      setSelecting(false);
    }
  };

  const toggleSelected = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const dismissHint = async (song) => {
    try {
      await dismissReadinessHint(song.id);
      patchSongs([song.id], { readiness_hint_dismissed_at: new Date().toISOString() });
    } catch {
      toast.error("Couldn't save: check your signal");
    }
  };

  const snoozeReviewCard = () => {
    const until = Date.now() + 30 * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(REVIEW_CARD_SNOOZE_KEY, String(until)); } catch {}
    setReviewCardSnoozedUntil(until);
  };

  const startEdit = (song) => {
    setEditingSong(song);
    setEditTab('details');
    setFormData({
      title: song.title,
      artist_original: song.artist_original,
      genre: song.genre,
      is_active: song.is_active,
      duration: secondsToMMSS(song.duration_seconds),
      notes: song.notes || '',
      lyrics: song.lyrics || '',
      chords: song.chords || '',
      is_original: song.is_original || false,
    });
    setWizardOpen(false);
  };

  const cancelEdit = () => {
    setEditingSong(null);
    setEditTab('details');
    setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '', is_original: false });
  };

  const openMenu = (e, songId) => {
    e.stopPropagation();
    const btn = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: btn.bottom + 4, right: window.innerWidth - btn.right });
    setMenuSongId(prev => prev === songId ? null : songId);
    setFabOpen(false);
  };

  const handleMenuAction = (action, song) => {
    setMenuSongId(null);
    if (action === 'edit') startEdit(song);
    else if (action === 'toggle') handleToggleActive(song.id);
    else if (action === 'delete') handleDelete(song.id, song.title);
  };

  const getUniqueGenres = () =>
    [...new Set(songs.map(s => s.genre).filter(Boolean))].sort();

  const secondsToMMSS = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const mmssToSeconds = (mmss) => {
    if (!mmss) return null;
    const match = mmss.match(/(\d+):(\d+)/);
    if (!match) return null;
    return parseInt(match[1]) * 60 + parseInt(match[2]);
  };

  const filteredSongs = songs.filter(song => {
    const matchesFilter =
      filter === 'all' ? true :
      filter === 'active' ? song.is_active :
      !song.is_active;
    const matchesGenre = genreFilter === 'all' ? true : song.genre === genreFilter;
    const matchesReadiness = readinessFilter === 'all' ? true : song.readiness === readinessFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      song.title.toLowerCase().includes(q) ||
      song.artist_original.toLowerCase().includes(q) ||
      (song.genre || '').toLowerCase().includes(q);
    return matchesFilter && matchesGenre && matchesReadiness && matchesSearch;
  });

  if (loading) return <PageLoader />;

  const activeCount = songs.filter(s => s.is_active).length;

  // The review card: a first audit (nothing reviewed yet), carrying on with one, or the
  // 6-monthly re-audit reminder. Never blocking, and it can be snoozed.
  const activeSongs = songs.filter(s => s.is_active);
  const reviewedCount = activeSongs.filter(s => s.readiness_reviewed_at).length;
  const dueCount = activeSongs.filter(s => dueForReview(s)).length;
  const reviewCard = offlineSince || Date.now() < reviewCardSnoozedUntil || !activeSongs.length ? null
    : reviewedCount === 0 ? { text: `Review your ${activeSongs.length} songs: mark each one Gig-ready, Rusty or Not ready, so the audience is only offered songs you're happy to play.`, cta: 'Start review', scope: 'unreviewed' }
    : reviewedCount < activeSongs.length && dueCount === activeSongs.length - reviewedCount ? { text: `${reviewedCount} of ${activeSongs.length} songs reviewed so far.`, cta: 'Carry on', scope: 'unreviewed' }
    : dueCount > 0 ? { text: `Time for a song review? ${dueCount} song${dueCount === 1 ? " hasn't" : "s haven't"} been looked at for 6 months.`, cta: 'Review them', scope: 'due' }
    : null;

  // "Worth rehearsing": rusty songs the audience has asked for, most requested first (spec §3.6)
  const worthRehearsing = activeSongs
    .filter(s => s.readiness === 'rusty' && s.times_requested > 0)
    .sort((a, b) => b.times_requested - a.times_requested || a.title.localeCompare(b.title));

  return (
    <div
      className="gr-fade"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme.background }}
      onPointerDown={() => { setMenuSongId(null); setFabOpen(false); }}
    >
      <OfflineBanner cachedAt={offlineSince} label="songs" onSignalBack={fetchSongs} />

      {/* ── Filter bar (pinned) ─────────────────────────────── */}
      <div
        style={{ flexShrink: 0, background: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
        onPointerDown={e => e.stopPropagation()}
      >
        <input
          type="text"
          placeholder="Search songs or artists..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
            borderRadius: 8, padding: '10px 12px', color: theme.text,
            fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 6 }}>
          {[['active', 'Active'], ['inactive', 'Hidden'], ['all', 'All']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              style={{
                flex: 1, background: filter === val ? theme.primary : theme.surfaceAlt,
                color: filter === val ? theme.onPrimary : theme.textSecondary,
                border: 'none', borderRadius: 8, padding: '8px 0',
                fontSize: 12, fontWeight: filter === val ? 700 : 400,
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {[['all', 'Any readiness'], ...READINESS_KEYS.map(k => [k, `${READINESS[k].emoji} ${READINESS[k].label}`])].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setReadinessFilter(val)}
              style={{
                flex: 1, background: readinessFilter === val ? theme.primary : theme.surfaceAlt,
                color: readinessFilter === val ? theme.onPrimary : theme.textSecondary,
                border: 'none', borderRadius: 8, padding: '7px 0',
                fontSize: 11, fontWeight: readinessFilter === val ? 700 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 7, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: 2 }}>
          {['All', ...getUniqueGenres()].map(g => {
            const isActive = g === 'All' ? genreFilter === 'all' : genreFilter === g;
            return (
              <button
                key={g}
                onClick={() => setGenreFilter(g === 'All' ? 'all' : g)}
                style={{
                  flexShrink: 0,
                  background: isActive ? theme.primary : theme.surfaceAlt,
                  color: isActive ? theme.onPrimary : theme.textSecondary,
                  border: isActive ? 'none' : `1px solid ${theme.border}`,
                  borderRadius: 20, padding: '6px 14px',
                  fontSize: 12, fontWeight: isActive ? 700 : 400,
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s',
                }}
              >
                {g}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Results count ───────────────────────────────────── */}
      <div style={{ flexShrink: 0, padding: '5px 14px 3px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace' }}>
          {filteredSongs.length} result{filteredSongs.length !== 1 ? 's' : ''} · {activeCount} active
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {!offlineSince && (
            <button
              onClick={() => { setSelecting(v => !v); setSelected(new Set()); }}
              style={{
                fontSize: 11, fontWeight: 600, color: selecting ? theme.onPrimary : theme.primary,
                background: selecting ? theme.primary : 'none', border: `1px solid ${theme.primary}`,
                borderRadius: 12, padding: '2px 10px', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {selecting ? 'Cancel' : 'Select'}
            </button>
          )}
          {!offlineSince && (
            <button
              onClick={() => navigate('/dashboard/songs/review')}
              style={{
                fontSize: 11, fontWeight: 600, color: theme.primary, background: 'none',
                border: `1px solid ${theme.primary}`, borderRadius: 12, padding: '2px 10px',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Review
            </button>
          )}
          {canEdit && songs.filter(s => !s.is_original && (!s.genre || !s.duration_seconds)).length > 0 && (
            <button
              onClick={openFillModal}
              style={{
                fontSize: 11, fontWeight: 600, color: '#1DB954',
                background: 'none', border: '1px solid #1DB954',
                borderRadius: 12, padding: '2px 10px', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Enrich {songs.filter(s => !s.is_original && (!s.genre || !s.duration_seconds)).length} songs
            </button>
          )}
          {canEdit && songs.filter(s => !s.is_original && !s.lyrics).length > 0 && (
            <button
              onClick={openLyricsModal}
              style={{
                fontSize: 11, fontWeight: 600, color: '#7c3aed',
                background: 'none', border: '1px solid #7c3aed',
                borderRadius: 12, padding: '2px 10px', cursor: 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <BookOpen size={11} /> Lyrics {songs.filter(s => !s.is_original && !s.lyrics).length}
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable list ─────────────────────────────────── */}
      <div
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        onPointerDown={e => e.stopPropagation()}
      >

        {reviewCard && (
          <div style={{ margin: '8px 12px', padding: '12px 14px', borderRadius: 12, background: theme.primary + '14', border: `1px solid ${theme.primary}40`, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.45 }}>{reviewCard.text}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate(`/dashboard/songs/review?scope=${reviewCard.scope}`)}
                style={{ background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {reviewCard.cta}
              </button>
              <button onClick={snoozeReviewCard}
                style={{ background: 'none', color: theme.textMuted, border: 'none', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                Not now
              </button>
            </div>
          </div>
        )}

        {worthRehearsing.length > 0 && !offlineSince && (
          <details style={{ margin: '8px 12px', padding: '10px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}` }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: theme.text }}>
              🎸 Worth rehearsing: {worthRehearsing.length} rusty song{worthRehearsing.length !== 1 ? 's' : ''} the audience asked for
            </summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
              {worthRehearsing.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: `1px solid ${theme.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: theme.textMuted }}>
                      {s.artist_original} · requested {s.times_requested === 1 ? 'once' : `${s.times_requested} times`}
                    </div>
                  </div>
                  <button onClick={() => applyReadiness([s.id], 'ready').then(ok => ok && toast.success(`${s.title}: Gig-ready`))}
                    style={{ flexShrink: 0, background: 'none', color: READINESS.ready.color, border: `1px solid ${READINESS.ready.color}`, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {READINESS.ready.emoji} Rehearsed
                  </button>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Empty state */}
        {filteredSongs.length === 0 && (
          <div style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', marginTop: 48, padding: '0 1rem' }}>
            No songs match your filters.
          </div>
        )}

        {/* Song rows */}
        {filteredSongs.map((song, i) => (
          <div
            key={song.id}
            style={{
              display: 'flex', alignItems: 'center',
              padding: '9px 12px 9px 14px',
              borderBottom: `1px solid ${theme.border}`,
              background: i % 2 === 0 ? theme.surface : theme.surfaceAlt,
              gap: 10, position: 'relative',
            }}
          >
            {/* Active accent bar */}
            <div style={{
              position: 'absolute', left: 0, top: 5, bottom: 5, width: 3,
              borderRadius: '0 2px 2px 0',
              background: song.is_active ? theme.primary : theme.border,
              transition: 'background 0.2s',
            }} />

            {selecting && (
              <input type="checkbox" checked={selected.has(song.id)} onChange={() => toggleSelected(song.id)}
                aria-label={`Select ${song.title}`}
                style={{ width: 20, height: 20, accentColor: theme.primary, flexShrink: 0, marginLeft: 4 }} />
            )}

            {/* Song info */}
            <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }} onClick={selecting ? () => toggleSelected(song.id) : undefined}>
              <div style={{
                color: song.is_active ? theme.text : theme.textMuted,
                fontWeight: 600, fontSize: 14,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'color 0.2s',
              }}>
                {song.title}
                {!song.is_active && (
                  <span style={{ marginLeft: 7, fontSize: 10, color: theme.textMuted, fontWeight: 400, letterSpacing: 0.5 }}>
                    HIDDEN
                  </span>
                )}
                {song.is_original && (
                  <span style={{ marginLeft: 7, fontSize: 10, color: theme.primary, fontWeight: 600, letterSpacing: 0.5 }}>
                    ORIGINAL
                  </span>
                )}
                {song.readiness && song.readiness !== 'ready' && (
                  <span style={{ marginLeft: 7, fontSize: 10, color: READINESS[song.readiness].color, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    {READINESS[song.readiness].emoji} {READINESS[song.readiness].label}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, flexWrap: 'wrap' }}>
                <span style={{ color: theme.textSecondary, fontSize: 12 }}>{song.artist_original}</span>
                {song.genre && (
                  <span style={{
                    background: theme.primary + '20', color: theme.primary,
                    borderRadius: 6, padding: '1px 6px', fontSize: 9,
                    fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                  }}>
                    {song.genre}
                  </span>
                )}
                {song.duration_seconds && (
                  <span style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace' }}>
                    {secondsToMMSS(song.duration_seconds)}
                  </span>
                )}
                {song.notes && (
                  <span style={{ color: theme.textMuted, fontSize: 11, fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FileText size={10} />
                    {song.notes}
                  </span>
                )}
                {song.last_played && (
                  <span style={{ color: theme.textMuted, fontSize: 11 }}>Played {formatDay(song.last_played)}</span>
                )}
              </div>
              {!selecting && !offlineSince && (() => {
                const hint = readinessHint(song);
                if (!hint) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    <span style={{ fontSize: 11, color: theme.textSecondary }}>{hint.text}</span>
                    <button onClick={() => applyReadiness([song.id], hint.suggest)}
                      style={{ fontSize: 11, fontWeight: 700, background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: 10, padding: '2px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Yes
                    </button>
                    <button onClick={() => dismissHint(song)}
                      style={{ fontSize: 11, background: 'none', color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Dismiss
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* ··· menu button */}
            {!selecting && <button
              onClick={e => openMenu(e, song.id)}
              onPointerDown={e => e.stopPropagation()}
              style={{
                background: 'none', border: 'none',
                color: menuSongId === song.id ? theme.primary : theme.textMuted,
                fontSize: 20, cursor: 'pointer',
                padding: '6px 8px', lineHeight: 1,
                flexShrink: 0, letterSpacing: 3, borderRadius: 6,
                minWidth: 36, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 0.15s',
              }}
            >
              ···
            </button>}
          </div>
        ))}

        {/* Spacer so last rows aren't hidden behind FAB */}
        <div style={{ height: 80 }} />
      </div>

      {/* ── Bulk readiness bar (while selecting) ─────────────── */}
      {selecting && (
        <div
          onPointerDown={e => e.stopPropagation()}
          style={{ position: 'fixed', left: 12, right: 12, bottom: 70, zIndex: 45, background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.25)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: theme.textSecondary }}>
            <span>{selected.size} selected: mark as</span>
            <button onClick={() => setSelected(selected.size === filteredSongs.length ? new Set() : new Set(filteredSongs.map(s => s.id)))}
              style={{ background: 'none', border: 'none', color: theme.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {selected.size === filteredSongs.length ? 'Select none' : `Select all ${filteredSongs.length}`}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {READINESS_KEYS.map(k => (
              <button key={k} disabled={!selected.size} onClick={() => bulkReadiness(k)}
                style={{ background: READINESS[k].color, color: '#fff', border: 'none', borderRadius: 10, padding: '9px 4px', fontSize: 12, fontWeight: 700,
                  cursor: selected.size ? 'pointer' : 'default', opacity: selected.size ? 1 : 0.45, fontFamily: 'inherit' }}>
                {READINESS[k].emoji} {READINESS[k].label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── FAB (add / import; not for performers) ──────────── */}
      {canEdit && !selecting && <div
        style={{ position: 'fixed', bottom: 70, right: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, zIndex: 40, pointerEvents: 'none' }}
        onPointerDown={e => e.stopPropagation()}
      >
        {fabOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, pointerEvents: 'all' }}>
            {[
              { label: 'Import CSV', onClick: () => { setFabOpen(false); setShowImportModal(true); }, bg: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, icon: null },
              { label: '+ Add Song', onClick: () => { setFabOpen(false); openAddWizard(); }, bg: theme.primary, color: theme.onPrimary, border: 'none', icon: null },
            ].map(({ label, onClick, bg, color, border, icon }) => (
              <button key={label} onClick={onClick} style={{
                background: bg, color, border, borderRadius: 24,
                padding: '10px 18px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', width: 140, justifyContent: 'center',
                boxShadow: '0 2px 14px rgba(0,0,0,0.25)',
                WebkitTapHighlightColor: 'transparent',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
              }}>
                {icon}{label}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); setFabOpen(v => !v); setMenuSongId(null); }}
          style={{
            width: 50, height: 50, borderRadius: '50%',
            background: fabOpen ? theme.surfaceAlt : theme.primary,
            color: fabOpen ? theme.textSecondary : theme.onPrimary,
            border: fabOpen ? `1px solid ${theme.border}` : 'none',
            fontSize: 26, fontWeight: 900,
            cursor: 'pointer', boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, transition: 'background 0.2s, transform 0.2s',
            transform: fabOpen ? 'rotate(45deg)' : 'none',
            WebkitTapHighlightColor: 'transparent', pointerEvents: 'all',
          }}
        >
          +
        </button>
      </div>}

      {/* ── Context menu popover ─────────────────────────────── */}
      {menuSongId !== null && (() => {
        const song = songs.find(s => s.id === menuSongId);
        if (!song) return null;
        const items = canEdit ? [
          { label: 'Edit', action: 'edit', color: theme.text },
          { label: song.is_active ? 'Hide' : 'Make Active', action: 'toggle', color: theme.text },
          { label: 'Delete', action: 'delete', color: '#dc3545' },
        ] : [
          { label: 'View lyrics & chords', action: 'edit', color: theme.text },
        ];
        return (
          <div
            onPointerDown={e => e.stopPropagation()}
            style={{
              position: 'fixed', top: menuPos.top, right: menuPos.right,
              zIndex: 60, background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 6px 24px rgba(0,0,0,0.2)', minWidth: 160,
            }}
          >
            {items.map(({ label, action, color }, i) => (
              <div
                key={action}
                onClick={() => handleMenuAction(action, song)}
                style={{
                  padding: '13px 16px', color, fontSize: 13, cursor: 'pointer',
                  borderBottom: i < items.length - 1 ? `1px solid ${theme.border}` : 'none',
                  background: 'transparent',
                }}
                onPointerEnter={e => e.currentTarget.style.background = theme.surfaceAlt}
                onPointerLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {label}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Add Song wizard modal ───────────────────────────── */}
      {wizardOpen && (() => {
        const inputStyle = {
          width: '100%', padding: '0.75rem', border: `2px solid ${theme.border}`,
          borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box',
          background: theme.surface, color: theme.text,
        };
        const labelStyle = {
          display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: theme.text, fontSize: '0.9rem',
        };
        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={e => { if (e.target === e.currentTarget) closeWizard(); }}
          >
            <div style={{ background: theme.surface, borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${theme.border}`, boxShadow: theme.isDark ? '0 20px 60px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.2)' }}>

              {/* Header */}
              <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', color: theme.text }}>New Song</h3>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
                    {wizardStep === 1 ? 'Title & Artist' : 'Details'}
                  </p>
                </div>
                <button onClick={closeWizard} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: 0 }}>×</button>
              </div>

              {/* Progress dots */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', padding: '0.85rem 1.5rem 0' }}>
                {[1, 2].map(s => (
                  <div key={s} style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: s <= wizardStep ? theme.primary : theme.border,
                    opacity: s < wizardStep ? 0.55 : 1,
                    transition: 'all 0.2s',
                    transform: s === wizardStep ? 'scale(1.4)' : 'scale(1)',
                  }} />
                ))}
              </div>

              {/* Step content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem 0.5rem' }}>
                {wizardStep === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Song Title <span style={{ color: '#dc3545' }}>*</span></label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                        autoFocus
                        placeholder="e.g. Mr Brightside"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        {formData.is_original ? 'Your Band / Artist Name' : 'Original Artist'}{' '}
                        <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.artist_original}
                        onChange={e => setFormData(f => ({ ...f, artist_original: e.target.value }))}
                        placeholder={formData.is_original ? 'e.g. Aria Vale' : 'e.g. The Killers'}
                        style={inputStyle}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={formData.is_original}
                        onChange={e => setFormData(f => ({ ...f, is_original: e.target.checked }))}
                        style={{ width: 16, height: 16, accentColor: theme.primary, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.88rem', color: theme.textSecondary }}>
                        Original composition — exclude from Spotify/Genius lookups
                      </span>
                    </label>
                  </div>
                )}
                {wizardStep === 2 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>Genre <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                        {!formData.is_original && (
                          <button
                            type="button"
                            onClick={lookupSpotifyGenre}
                            disabled={genreLookingUp || !formData.title}
                            style={{
                              fontSize: '0.75rem', fontWeight: 700,
                              padding: '3px 10px', borderRadius: 20,
                              border: '1px solid #1DB954', color: '#1DB954',
                              background: 'none', cursor: genreLookingUp ? 'wait' : 'pointer',
                              fontFamily: 'inherit', opacity: !formData.title ? 0.4 : 1,
                            }}
                          >
                            {genreLookingUp ? 'Looking up…' : <><Music size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Spotify Lookup</>}
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={formData.genre}
                        onChange={e => setFormData(f => ({ ...f, genre: e.target.value }))}
                        autoFocus
                        placeholder="e.g. Rock, Pop, Folk"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Duration <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                      <input
                        type="text"
                        value={formData.duration}
                        onChange={e => setFormData(f => ({ ...f, duration: e.target.value }))}
                        placeholder="e.g. 3:45"
                        pattern="[0-9]+:[0-5][0-9]"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                      <input
                        type="text"
                        value={formData.notes}
                        onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                        placeholder="e.g. Key of G, Capo 2 — slow intro"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  onClick={closeWizard}
                  style={{ padding: '0.7rem 1.1rem', background: 'none', color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {wizardStep > 1 && (
                    <button
                      onClick={() => setWizardStep(s => s - 1)}
                      style={{ padding: '0.7rem 1.25rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Back
                    </button>
                  )}
                  {wizardStep < 2 ? (
                    <button
                      onClick={() => {
                        if (!formData.title.trim()) { toast.error('Song title is required'); return; }
                        if (!formData.artist_original.trim()) { toast.error('Original artist is required'); return; }
                        setWizardStep(2);
                      }}
                      style={{ padding: '0.7rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handleAddSong}
                      disabled={submitting}
                      style={{ padding: '0.7rem 1.5rem', background: submitting ? '#aaa' : theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                    >
                      {submitting ? 'Adding…' : 'Add Song'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit Song modal ──────────────────────────────────── */}
      {editingSong && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div style={{
            background: theme.surface, borderRadius: '14px',
            width: '100%', maxWidth: '560px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem 0' }}>
              <div>
                <h3 style={{ margin: 0, color: theme.text }}>{canEdit ? 'Edit Song' : 'Song'}</h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: theme.textMuted }}>{editingSong.title}</p>
              </div>
              <button onClick={cancelEdit} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: '0.2rem 0.4rem' }}>×</button>
            </div>

            <div style={{ display: 'flex', borderBottom: `2px solid ${theme.border}`, margin: '1rem 1.5rem 0', gap: '0.25rem' }}>
              {[['details', 'Details'], ['lyrics', 'Lyrics & Chords']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setEditTab(key)}
                  style={{
                    padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '0.9rem', fontWeight: editTab === key ? '700' : '400',
                    color: editTab === key ? theme.primary : theme.textMuted,
                    borderBottom: editTab === key ? `2px solid ${theme.primary}` : '2px solid transparent',
                    marginBottom: '-2px',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleUpdateSong} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
                {editTab === 'details' && (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={fieldLabelStyle}>Readiness</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        {READINESS_KEYS.map(k => {
                          const on = (editingSong.readiness || 'ready') === k;
                          return (
                            <button type="button" key={k} onClick={() => !on && applyReadiness([editingSong.id], k)}
                              style={{ padding: '0.6rem 0.25rem', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
                                fontWeight: on ? 700 : 400, background: on ? READINESS[k].color : theme.surfaceAlt,
                                color: on ? '#fff' : theme.textSecondary, border: on ? 'none' : `1px solid ${theme.border}` }}>
                              {READINESS[k].emoji} {READINESS[k].label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: theme.textMuted, marginTop: '0.35rem' }}>
                        Saved straight away. {editingSong.last_played ? `Last played ${formatDay(editingSong.last_played)}.` : 'Not played at a gig yet.'}
                      </div>
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Song Title *</label>
                      <input type="text" readOnly={!canEdit} placeholder="Enter song title" value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Original Artist *</label>
                      <input type="text" readOnly={!canEdit} placeholder="Enter artist name" value={formData.artist_original}
                        onChange={e => setFormData({ ...formData, artist_original: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Genre</label>
                      <input type="text" readOnly={!canEdit} placeholder="e.g., Rock, Pop, Folk" value={formData.genre}
                        onChange={e => setFormData({ ...formData, genre: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Duration</label>
                      <input type="text" readOnly={!canEdit} placeholder="e.g., 3:45 (MM:SS)" value={formData.duration}
                        onChange={e => setFormData({ ...formData, duration: e.target.value })}
                        pattern="[0-9]+:[0-5][0-9]" style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Notes</label>
                      <input type="text" readOnly={!canEdit} placeholder="e.g., Key of G, Capo 2 — slow intro" value={formData.notes}
                        onChange={e => setFormData({ ...formData, notes: e.target.value })} style={fieldStyle} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none', marginTop: '0.25rem' }}>
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={formData.is_original || false}
                        onChange={e => setFormData({ ...formData, is_original: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: theme.primary, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.88rem', color: theme.textSecondary }}>
                        Original composition — exclude from Spotify/Genius lookups
                      </span>
                    </label>
                  </div>
                )}
                {editTab === 'lyrics' && (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={fieldLabelStyle}>Chords / Chart</label>
                      <textarea readOnly={!canEdit}
                        placeholder={`e.g.\nVerse:\n[G] Here comes the sun [D]\n[A] Little darling [G]`}
                        value={formData.chords}
                        onChange={e => setFormData({ ...formData, chords: e.target.value })}
                        rows={8}
                        style={{ ...fieldStyle, fontFamily: 'monospace', fontSize: '0.88rem', resize: 'vertical', lineHeight: 1.6 }}
                      />
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <label style={{ ...fieldLabelStyle, marginBottom: 0 }}>Lyrics</label>
                        {canEdit && !formData.is_original && (
                          <button
                            type="button"
                            onClick={lookupGeniusLyrics}
                            disabled={lyricsLookingUp || !formData.title || !formData.artist_original}
                            style={{
                              fontSize: 11, fontWeight: 600, color: lyricsLookingUp ? theme.textMuted : '#7c3aed',
                              background: 'none', border: `1px solid ${lyricsLookingUp ? theme.border : '#7c3aed'}`,
                              borderRadius: 10, padding: '2px 8px', cursor: lyricsLookingUp ? 'default' : 'pointer',
                              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3, opacity: (!formData.title || !formData.artist_original) ? 0.4 : 1,
                            }}
                          >
                            <BookOpen size={11} /> {lyricsLookingUp ? 'Looking up…' : 'Fetch from Genius'}
                          </button>
                        )}
                      </div>
                      <textarea readOnly={!canEdit}
                        placeholder="Paste full lyrics here..."
                        value={formData.lyrics}
                        onChange={e => setFormData({ ...formData, lyrics: e.target.value })}
                        rows={10}
                        style={{ ...fieldStyle, fontSize: '0.9rem', resize: 'vertical', lineHeight: 1.7 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}` }}>
                {canEdit && (
                  <button type="submit" style={{ padding: '0.75rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                    Update Song
                  </button>
                )}
                <button type="button" onClick={cancelEdit} style={{ padding: '0.75rem 1.5rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer' }}>
                  {canEdit ? 'Cancel' : 'Close'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Fill Missing Genres Modal ────────────────────────── */}
      {fillModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div style={{
            background: theme.surface, borderRadius: '16px 16px 0 0',
            width: '100%', maxWidth: 600, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>Enrich Songs</div>
                <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 2 }}>
                  {fillFetching
                    ? `Looking up… (${fillResults.filter(r => !r.loading).length}/${fillResults.length})`
                    : `${fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length} of ${fillResults.length} ready to save`
                  }
                </div>
              </div>
              <button
                onClick={() => setFillModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.5rem', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Select all / none */}
            {!fillFetching && (
              <div style={{ padding: '6px 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                <button
                  onClick={() => setFillResults(prev => prev.map(r => ({ ...r, accepted: !!r.suggestedGenre.trim() })))}
                  style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >Select all</button>
                <button
                  onClick={() => setFillResults(prev => prev.map(r => ({ ...r, accepted: false })))}
                  style={{ fontSize: 11, color: theme.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >Deselect all</button>
              </div>
            )}

            {/* Song rows */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem' }}>
              {fillResults.map((r, i) => (
                <div key={r.song.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.7rem 0.5rem',
                  borderBottom: `1px solid ${theme.border}`,
                }}>
                  <input
                    type="checkbox"
                    checked={r.accepted}
                    disabled={r.loading || (r.error && !r.durationSecs)}
                    onChange={e => setFillResults(prev => prev.map((x, j) => j === i ? { ...x, accepted: e.target.checked } : x))}
                    style={{ width: 17, height: 17, flexShrink: 0, accentColor: theme.primary }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text }}>{r.song.title}</div>
                    <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{r.song.artist_original}</div>
                  </div>
                  {r.loading ? (
                    <span style={{ color: theme.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>Looking up…</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {!r.song.genre && (
                        r.error ? (
                          <span style={{ color: '#dc3545', fontSize: '0.75rem' }}>No match</span>
                        ) : (
                          <input
                            type="text"
                            value={r.suggestedGenre}
                            onChange={e => setFillResults(prev => prev.map((x, j) => j === i ? { ...x, suggestedGenre: e.target.value } : x))}
                            style={{
                              width: 100, padding: '4px 8px',
                              border: `1px solid ${r.suggestedGenre === 'Other' ? theme.border : theme.primary}`,
                              borderRadius: 6, fontSize: '0.82rem',
                              background: theme.surfaceAlt, color: theme.text,
                              boxSizing: 'border-box',
                            }}
                          />
                        )
                      )}
                      {!r.song.duration_seconds && r.durationSecs && (
                        <span style={{ fontSize: '0.75rem', color: theme.textMuted, background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '3px 6px' }}>
                          {Math.floor(r.durationSecs / 60)}:{(r.durationSecs % 60).toString().padStart(2, '0')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button
                onClick={() => setFillModalOpen(false)}
                style={{ padding: '0.65rem 1.25rem', background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', color: theme.textSecondary, fontWeight: 600, fontFamily: 'inherit' }}
              >Cancel</button>
              <button
                onClick={saveFill}
                disabled={savingFill || fillFetching || fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length === 0}
                style={{
                  padding: '0.65rem 1.5rem', background: theme.navGradient,
                  color: theme.onPrimary, border: 'none', borderRadius: 8,
                  fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
                  opacity: (savingFill || fillFetching || fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length === 0) ? 0.5 : 1,
                }}
              >
                {savingFill ? 'Saving…' : `Save ${fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length} songs`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fetch Lyrics Modal ───────────────────────────────── */}
      {lyricsModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div style={{
            background: theme.surface, borderRadius: '16px 16px 0 0',
            width: '100%', maxWidth: 600, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>Fetch Lyrics</div>
                <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 2 }}>
                  {lyricsFetching
                    ? `Looking up… (${lyricsResults.filter(r => !r.loading).length}/${lyricsResults.length})`
                    : `${lyricsResults.filter(r => r.accepted && r.lyrics).length} of ${lyricsResults.length} ready to save`}
                </div>
              </div>
              <button onClick={() => setLyricsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.5rem', lineHeight: 1 }}>×</button>
            </div>

            {!lyricsFetching && (
              <div style={{ padding: '6px 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                <button onClick={() => setLyricsResults(prev => prev.map(r => ({ ...r, accepted: !!r.lyrics })))} style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Select all</button>
                <button onClick={() => setLyricsResults(prev => prev.map(r => ({ ...r, accepted: false })))} style={{ fontSize: 11, color: theme.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Deselect all</button>
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem' }}>
              {lyricsResults.map((r, i) => (
                <div key={r.song.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.5rem', borderBottom: `1px solid ${theme.border}` }}>
                  <input
                    type="checkbox"
                    checked={r.accepted}
                    disabled={r.loading || r.error}
                    onChange={e => setLyricsResults(prev => prev.map((x, j) => j === i ? { ...x, accepted: e.target.checked } : x))}
                    style={{ width: 17, height: 17, flexShrink: 0, accentColor: theme.primary }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text }}>{r.song.title}</div>
                    <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{r.song.artist_original}</div>
                  </div>
                  {r.loading ? (
                    <span style={{ color: theme.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>Looking up…</span>
                  ) : r.error || !r.lyrics ? (
                    <span style={{ color: '#dc3545', fontSize: '0.75rem', flexShrink: 0 }}>No match</span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: theme.textMuted, flexShrink: 0, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.matchedTitle !== r.song.title ? `"${r.matchedTitle}"` : '✓ matched'}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button onClick={() => setLyricsModalOpen(false)} style={{ padding: '0.65rem 1.25rem', background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', color: theme.textSecondary, fontWeight: 600, fontFamily: 'inherit' }}>Cancel</button>
              <button
                onClick={saveLyrics}
                disabled={savingLyrics || lyricsFetching || lyricsResults.filter(r => r.accepted && r.lyrics).length === 0}
                style={{
                  padding: '0.65rem 1.5rem', background: '#7c3aed',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
                  opacity: (savingLyrics || lyricsFetching || lyricsResults.filter(r => r.accepted && r.lyrics).length === 0) ? 0.5 : 1,
                }}
              >
                {savingLyrics ? 'Saving…' : `Save ${lyricsResults.filter(r => r.accepted && r.lyrics).length} lyrics`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Import Modal ─────────────────────────────────── */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => { fetchSongs(); setShowImportModal(false); }}
        onImportComplete={async (importedSongs) => {
          if (importedSongs && importedSongs.length > 0) {
            setSongs(prev => {
              const existingIds = new Set(prev.map(s => s.id));
              const toAdd = importedSongs.filter(s => !existingIds.has(s.id));
              return [...prev, ...toAdd].sort((a, b) => a.title.localeCompare(b.title));
            });
          } else {
            await fetchSongs();
          }
          setFilter('active');
          setGenreFilter('all');
          setSearchTerm('');
          setShowImportModal(false);
        }}
        theme={theme}
      />

    </div>
  );
}

export default SongManagement;
